<template>
    <section class="rounded-[12.8px] border border-gray-200 bg-white p-[21px]">
        <div class="mb-4">
            <p class="font-medium text-gray-900">
                {{ t("vue.dashboard_overview_activity_title") }}
            </p>
            <p class="mt-1 text-[13px] leading-[18px] text-gray-500">
                {{ t("vue.dashboard_overview_activity_subtitle") }}
            </p>
        </div>

        <p v-if="items.length === 0" class="text-sm text-gray-600">
            {{ t("vue.dashboard_overview_activity_empty") }}
        </p>

        <div v-else class="grid">
            <div
                v-for="(item, index) in items"
                :key="`${item.eventCode}-${item.occurredAt}-${index}`"
                class="flex min-h-[52px] items-center gap-3 border-t border-gray-200 py-2 first:border-t-0 first:pt-0"
            >
                <div
                    class="flex h-9 w-9 shrink-0 items-center justify-center rounded-[12.8px] bg-gray-100"
                >
                    <component
                        :is="resolveIcon(item.icon)"
                        class="h-[18px] w-[18px] text-gray-500"
                    />
                </div>
                <p
                    class="min-w-0 flex-1 truncate text-[13px] leading-[18px] text-gray-600"
                >
                    {{ resolveMessage(item) }}
                </p>
                <p class="shrink-0 text-[13px] leading-[18px] text-gray-400">
                    {{ resolveRelativeTime(item.occurredAt) }}
                </p>
            </div>
        </div>
    </section>
</template>

<script lang="ts" setup>
import type { Component } from "vue";
import { formatDistanceToNow } from "date-fns";
import { cs, enUS } from "date-fns/locale";
import {
    CreditCardIcon,
    GlobeAltIcon,
    ShieldCheckIcon,
    UserGroupIcon,
} from "@heroicons/vue/24/outline";
import { useTranslations } from "@/composables/useTranslations";
import type {
    DashboardActivityIcon,
    IActivityItem,
} from "@/types/Dashboard/IDashboardOverview";

defineProps<{
    items: IActivityItem[];
}>();

const { t, locale } = useTranslations();

const resolveIcon = (icon: DashboardActivityIcon): Component => {
    switch (icon) {
        case "domain":
            return GlobeAltIcon;
        case "shield":
            return ShieldCheckIcon;
        case "team":
            return UserGroupIcon;
        default:
            return CreditCardIcon;
    }
};

const resolveMessage = (item: IActivityItem): string =>
    t(`vue.dashboard_overview_activity_${item.eventCode}`, {
        subject: item.subject ?? "",
    });

const resolveRelativeTime = (occurredAt: string): string =>
    formatDistanceToNow(new Date(occurredAt), {
        addSuffix: true,
        locale: locale.value === "cs" ? cs : enUS,
    });
</script>
